import React, { useMemo, useState, useEffect, useCallback } from "react";
import { JSX } from "react/jsx-runtime";

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);

// Unified input sanitization (shared by Data TB and all money-like inputs)
// Rules (mirrors previous Data (TB) field behavior):
// - Allow digits and at most one decimal separator (comma preferred, dot accepted and normalized)
// - Preserve a trailing separator while the user is typing (to allow entering decimals)
// - Limit integer part to 4 digits (0-9999)
// - Limit decimals to 2
// - Clamp numeric value to max 9999,99 (unless user currently has trailing comma)
// - Strip leading zeros except single 0
export const sanitizeNumericInput = (raw: string): string => {
  if (raw === "") return "";
  const endsWithSep = /[.,]$/.test(raw);
  let cleaned = raw.replace(/[^0-9.,]/g, "");
  // Normalize any dots to commas and collapse multiples
  cleaned = cleaned.replace(/\./g, ",");
  // Keep only first comma
  const firstComma = cleaned.indexOf(",");
  if (firstComma !== -1) {
    const before = cleaned.slice(0, firstComma + 1);
    const after = cleaned.slice(firstComma + 1).replace(/,/g, "");
    cleaned = before + after;
  }
  // Split integer/decimal
  let [intPart, decPart = ""] = cleaned.split(",");
  intPart = intPart.replace(/^0+(?=\d)/, "");
  if (intPart === "") intPart = "0";
  if (intPart.length > 4) intPart = intPart.slice(0, 4);
  decPart = decPart.slice(0, 2);
  if (decPart) {
    cleaned = `${intPart},${decPart}`;
  } else if (endsWithSep) {
    cleaned = `${intPart},`;
  } else {
    cleaned = intPart;
  }
  // Clamp only finalized numeric (not when user just typed comma)
  if (!cleaned.endsWith(",")) {
    const numeric = parseNumberInput(cleaned);
    if (numeric > 9999.99) {
      cleaned = "9999,99";
    }
  }
  return cleaned;
};

// Finalize generic numeric (usage TB): keep prior trimmed style (used for Data TB usage only)
export const finalizeNumericInput = (value: string): string => {
  if (value === "") return "0";
  let numeric = parseNumberInput(value);
  if (numeric > 9999.99) numeric = 9999.99;
  const fixed = numeric.toFixed(2);
  // For usage we still trim trailing zeros for cleanliness
  return fixed
    .replace(/\.00$/, "")
    .replace(/(\.[0-9])0$/, "$1")
    .replace(".", ",");
};

// Currency-specific finalization: if fractional part exists, always show exactly two decimals; whole numbers show none
export const finalizeCurrencyInput = (value: string): string => {
  if (value === "") return "0";
  let numeric = parseNumberInput(value);
  if (numeric > 9999.99) numeric = 9999.99;
  if (Number.isInteger(numeric)) {
    return Math.trunc(numeric).toString().replace(".", ",");
  }
  return numeric.toFixed(2).replace(".", ",");
};

// parse a string with comma or dot as decimal separator
const parseNumberInput = (s: string) => {
  const normalized = s.trim().replace(/\s+/g, "").replace(",", ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

// format computed decimal back to comma form (2 decimals)
const toCommaFixed2 = (n: number) => n.toFixed(2).replace(".", ",");

// Currency display helper (computed values): force two decimals if any fractional part, else integer
const numberToCurrencyDisplay = (n: number) => {
  if (Number.isInteger(n)) return Math.trunc(n).toString();
  return n.toFixed(2).replace(".", ",");
};

export default function PriceCalculator(): JSX.Element {
  // Defaults (used for showing restore buttons)
  const DIST_DEFAULTS = useMemo(() => ({
    license: "7,69",
    data: "169",
    min: "284",
    margin: "30"
  }), []);

  useEffect(() => {
    document.title = "Romexis Image Cloud Price Calculator";
  }, []);

  // Detect touch-capable device (basic heuristic)
  const isTouch = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  }, []);

  // Select all text on focus (mobile usability: tap once to overwrite)
  const handleSelectAll: React.FocusEventHandler<HTMLInputElement> = useCallback((e) => {
    if (!isTouch) return; // only on touch devices to avoid surprising desktop users
    // Defer selection to ensure it occurs after focus event bubbling.
    requestAnimationFrame(() => {
      try { e.target.select(); } catch { /* ignore */ }
    });
  }, [isTouch]);

  // Distributor inputs (defaults with comma)
  const [licenseCostInput, setLicenseCostInput] = useState<string>(DIST_DEFAULTS.license);
  const [dataCostInput, setDataCostInput] = useState<string>(DIST_DEFAULTS.data);
  const [minPriceInput, setMinPriceInput] = useState<string>(DIST_DEFAULTS.min);

  // Margin & customer override state
  const [distMarginCustInput, setDistMarginCustInput] = useState<string>(DIST_DEFAULTS.margin);
  const [licenseCostCustInput, setLicenseCostCustInput] = useState<string>("");
  const [dataCostCustInput, setDataCostCustInput] = useState<string>("");
  const [minPriceCustInput, setMinPriceCustInput] = useState<string>("");

  // Customer information
  // Licenses requirements:
  // 1. Non-integer values are floored on blur
  // 2. Negative numbers become 0
  // 3. Max 4 digits
  // 4. Allow clearing all characters (empty string treated as 0 in calculations)
  const [licensesInput, setLicensesInput] = useState<string>("15");
  const [dataTbInput, setDataTbInput] = useState<string>("2"); // default TB input

  // Parsed licenses (integer >=0, at most 4 digits)
  const licenses = useMemo(() => {
    if (!licensesInput) return 0;
    // normalize decimal separator (comma -> dot) for potential float before floor
    const normalized = licensesInput.replace(/,/g, ".");
    let n = Math.floor(Number(normalized));
    if (!Number.isFinite(n) || n < 0) n = 0;
    // enforce 4 digit max
    if (n > 9999) {
      n = Number(n.toString().slice(0, 4));
    }
    return n;
  }, [licensesInput]);

  // Parsed distributor values
  const licenseCost = useMemo(() => parseNumberInput(licenseCostInput), [licenseCostInput]);
  const dataCost = useMemo(() => parseNumberInput(dataCostInput), [dataCostInput]);
  const minPrice = useMemo(() => parseNumberInput(minPriceInput), [minPriceInput]);

  // Parsed usage data (rounded up later, min 1 TB)
  const parsedDataTb = useMemo(() => parseNumberInput(dataTbInput), [dataTbInput]);
  const chargedDataTb = useMemo(() => Math.max(1, Math.ceil(parsedDataTb)), [parsedDataTb]);

  // Distributor totals
  const licenseTotal = useMemo(() => Math.max(0, licenses) * licenseCost, [licenses, licenseCost]);
  const dataTotal = useMemo(() => chargedDataTb * dataCost, [chargedDataTb, dataCost]);
  const subtotal = useMemo(() => licenseTotal + dataTotal, [licenseTotal, dataTotal]);
  const minPricePositive = Math.max(0, minPrice);
  const appliedTotal = useMemo(() => Math.max(subtotal, minPricePositive), [subtotal, minPricePositive]);
  const isBelowMin = subtotal < minPricePositive;

  // Margin percent (if not overridden)
  const distMarginPercent = useMemo(
    () => (distMarginCustInput === "-" ? 0 : parseNumberInput(distMarginCustInput) / 100),
    [distMarginCustInput]
  );

  // Computed customer values (when margin active)
  const licenseCostCustComputed = useMemo(
    () => licenseCost * (1 + distMarginPercent),
    [licenseCost, distMarginPercent]
  );
  const dataCostCustComputed = useMemo(
    () => dataCost * (1 + distMarginPercent),
    [dataCost, distMarginPercent]
  );
  const minPriceCustComputed = useMemo(
    () => minPrice * (1 + distMarginPercent),
    [minPrice, distMarginPercent]
  );

  // Keep customer fields synced while margin is active (not overridden)
  useEffect(() => {
    if (distMarginCustInput === "-") return;
  const lic = numberToCurrencyDisplay(licenseCostCustComputed);
  const dat = numberToCurrencyDisplay(dataCostCustComputed);
  const min = numberToCurrencyDisplay(minPriceCustComputed);
    if (licenseCostCustInput !== lic) setLicenseCostCustInput(lic);
    if (dataCostCustInput !== dat) setDataCostCustInput(dat);
    if (minPriceCustInput !== min) setMinPriceCustInput(min);
  }, [
    distMarginCustInput,
    licenseCostCustComputed,
    dataCostCustComputed,
    minPriceCustComputed,
    licenseCostCustInput,
    dataCostCustInput,
    minPriceCustInput
  ]);

  // Effective customer values (computed or manual)
  const licenseCostCust =
    distMarginCustInput === "-" ? parseNumberInput(licenseCostCustInput || "0") : licenseCostCustComputed;
  const dataCostCust =
    distMarginCustInput === "-" ? parseNumberInput(dataCostCustInput || "0") : dataCostCustComputed;
  const minPriceCust =
    distMarginCustInput === "-" ? parseNumberInput(minPriceCustInput || "0") : minPriceCustComputed;

  // Customer totals
  const licenseTotalCust = useMemo(
    () => Math.max(0, licenses) * licenseCostCust,
    [licenses, licenseCostCust]
  );
  const dataTotalCust = useMemo(
    () => chargedDataTb * dataCostCust,
    [chargedDataTb, dataCostCust]
  );
  const subtotalCust = useMemo(
    () => licenseTotalCust + dataTotalCust,
    [licenseTotalCust, dataTotalCust]
  );
  const minPriceCustPositive = Math.max(0, minPriceCust);
  const appliedTotalCust = useMemo(
    () => Math.max(subtotalCust, minPriceCustPositive),
    [subtotalCust, minPriceCustPositive]
  );
  const isBelowMinCust = subtotalCust < minPriceCustPositive;

  // Actual margin when customer prices are manually overridden (based on final totals)
  const actualMarginPercent = useMemo(() => {
    if (distMarginCustInput !== "-") return null;
    if (appliedTotal <= 0) return null;
    return ((appliedTotalCust - appliedTotal) / appliedTotal) * 100;
  }, [distMarginCustInput, appliedTotalCust, appliedTotal]);

  // Absolute distributor net margin in EUR (difference between customer and distributor totals)
  const distributorNetMarginEur = useMemo(
    () => Math.max(0, appliedTotalCust - appliedTotal),
    [appliedTotalCust, appliedTotal]
  );

  const formatPercent = (v: number) => toCommaFixed2(v) + " %";

  // Dirty flags for showing "Restore defaults"
  const distributorDirty = (
    licenseCostInput !== DIST_DEFAULTS.license ||
    dataCostInput !== DIST_DEFAULTS.data ||
    minPriceInput !== DIST_DEFAULTS.min
  );

  const customerDirty = (
    distMarginCustInput !== DIST_DEFAULTS.margin ||
    distMarginCustInput === "-" // manual override
  );

  const restoreDistributorDefaults = () => {
    setLicenseCostInput(DIST_DEFAULTS.license);
    setDataCostInput(DIST_DEFAULTS.data);
    setMinPriceInput(DIST_DEFAULTS.min);
  };

  const restoreCustomerDefaults = () => {
    setDistMarginCustInput(DIST_DEFAULTS.margin);
    setLicenseCostCustInput("");
    setDataCostCustInput("");
    setMinPriceCustInput("");
  };

  return (
    <main className="calc-shell" role="main">
      <section className="card" aria-labelledby="title">
        <h1 id="title" className="title">Romexis Image Cloud Price Calculator</h1>

        {/* Distributor price group */}
        <div className="group distributor" role="group" aria-labelledby="dist-price-label">
          <h2 id="dist-price-label" className="group-title">Distributor price</h2>
          {distributorDirty && (
            <button
              type="button"
              className="restore-btn"
              onClick={restoreDistributorDefaults}
              aria-label="Restore distributor defaults"
              title="Restore defaults"
            >
              ↺
            </button>
          )}
            <div className="row costs">
              <label>
                <span className="label-text">License</span>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.,]*"
                  value={licenseCostInput}
                  onChange={(e) => setLicenseCostInput(sanitizeNumericInput(e.target.value))}
                  onBlur={() => setLicenseCostInput((v) => finalizeCurrencyInput(v))}
                  onFocus={handleSelectAll}
                />
              </label>
                <label>
                  <span className="label-text">Data / TB</span>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.,]*"
                  value={dataCostInput}
                  onChange={(e) => setDataCostInput(sanitizeNumericInput(e.target.value))}
                  onBlur={() => setDataCostInput((v) => finalizeCurrencyInput(v))}
                  onFocus={handleSelectAll}
                />
              </label>
                <label>
                  <span className="label-text">Min. price</span>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.,]*"
                  value={minPriceInput}
                  onChange={(e) => setMinPriceInput(sanitizeNumericInput(e.target.value))}
                  onBlur={() => setMinPriceInput((v) => finalizeCurrencyInput(v))}
                  onFocus={handleSelectAll}
                />
              </label>
            </div>
        </div>

        {/* Customer price group */}
        <div className="group customer" role="group" aria-labelledby="cust-price-label">
          <h2 id="cust-price-label" className="group-title">Customer price</h2>
          {customerDirty && (
            <button
              type="button"
              className="restore-btn"
              onClick={restoreCustomerDefaults}
              aria-label="Restore customer defaults"
              title="Restore defaults"
            >
              ↺
            </button>
          )}
          <div className="row costs">
            <label>
              <span className="label-text">License</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                title={
                  distMarginCustInput === "-"
                    ? "Manually overridden customer license"
                    : "Calculated: distributor license + margin"
                }
                value={
                  distMarginCustInput === "-"
                    ? licenseCostCustInput
                    : numberToCurrencyDisplay(licenseCostCustComputed)
                }
                onChange={(e) => {
                  setDistMarginCustInput("-");
                  setLicenseCostCustInput(sanitizeNumericInput(e.target.value));
                }}
                onBlur={() => {
                  if (distMarginCustInput === "-") {
                    setLicenseCostCustInput((v) => finalizeCurrencyInput(v));
                  }
                }}
                onFocus={handleSelectAll}
              />
            </label>
              <label>
                <span className="label-text">Data / TB</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                title={
                  distMarginCustInput === "-"
                    ? "Manually overridden customer data cost"
                    : "Calculated: distributor data cost + margin"
                }
                value={
                  distMarginCustInput === "-"
                    ? dataCostCustInput
                    : numberToCurrencyDisplay(dataCostCustComputed)
                }
                onChange={(e) => {
                  setDistMarginCustInput("-");
                  setDataCostCustInput(sanitizeNumericInput(e.target.value));
                }}
                onBlur={() => {
                  if (distMarginCustInput === "-") {
                    setDataCostCustInput((v) => finalizeCurrencyInput(v));
                  }
                }}
                onFocus={handleSelectAll}
              />
            </label>
              <label>
                <span className="label-text">Min. price</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                title={
                  distMarginCustInput === "-"
                    ? "Manually overridden customer minimum price"
                    : "Calculated: distributor minimum + margin"
                }
                value={
                  distMarginCustInput === "-"
                    ? minPriceCustInput
                    : numberToCurrencyDisplay(minPriceCustComputed)
                }
                onChange={(e) => {
                  setDistMarginCustInput("-");
                  setMinPriceCustInput(sanitizeNumericInput(e.target.value));
                }}
                onBlur={() => {
                  if (distMarginCustInput === "-") {
                    setMinPriceCustInput((v) => finalizeCurrencyInput(v));
                  }
                }}
                onFocus={handleSelectAll}
              />
            </label>
            <label>
                <span className="label-text">Margin (%)</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,-]*"
                value={distMarginCustInput}
                title="Margin applied to distributor prices to derive customer prices (set to '-' when manually overridden)"
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  if (raw === "-") {
                    setDistMarginCustInput("-");
                  } else {
                    // Keep legacy simple handling for percent (not applying money-like clamp)
                    setDistMarginCustInput(sanitizeNumericInput(raw));
                  }
                }}
                onFocus={handleSelectAll}
              />
            </label>
          </div>
          {actualMarginPercent !== null && (
            <div className="actual-margin-note" role="note">
              Actual margin (based on totals): {formatPercent(actualMarginPercent)}
            </div>
          )}
        </div>

        {/* Customer information group */}
        <div className="group customer-info" role="group" aria-labelledby="cust-info-label">
          <h2 id="cust-info-label" className="group-title">Customer information</h2>
          <div className="row costs">
            <label>
              <span className="label-text">Licenses</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={licensesInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setLicensesInput("");
                    return;
                  }
                  // keep only digits
                  let cleaned = raw.replace(/\D/g, "");
                  if (cleaned.length > 4) cleaned = cleaned.slice(0,4);
                  setLicensesInput(cleaned);
                }}
                onBlur={() => {
                  // If empty, set to 0 instead of relying on placeholder (no ghost text)
                  if (licensesInput === "") {
                    setLicensesInput("0");
                    return;
                  }
                  let n = licenses; // already parsed & clamped logic above
                  if (!Number.isFinite(n) || n < 0) n = 0;
                  if (n > 9999) n = Number(n.toString().slice(0,4));
                  setLicensesInput(n.toString());
                }}
                onFocus={handleSelectAll}
              />
            </label>
            <label>
              <span className="label-text">
                <span className="nowrap-label">Data (TB)
                <span className="tooltip">
                  <button
                    type="button"
                    className="tip-btn"
                    aria-describedby="tip-data-charging"
                    tabIndex={-1}
                    aria-hidden="true"
                  >i</button>
                  <span id="tip-data-charging" role="tooltip" className="tip-content">
                    Charged per TB — fractions round up (minimum 1 TB; e.g. 0 → 1 TB, 3,3 → 4 TB)
                  </span>
                </span></span>
              </span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                value={dataTbInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setDataTbInput(""); return; }
                  setDataTbInput(sanitizeNumericInput(raw));
                }}
                onBlur={() => setDataTbInput((v) => finalizeNumericInput(v))}
                onFocus={handleSelectAll}
              />
            </label>
            <label aria-hidden="true" className="visually-hidden" />
            <label aria-hidden="true" className="visually-hidden" />
          </div>
        </div>

        <div className="summary" aria-live="polite">
          <div className="summary-header" aria-hidden="true">
            <div></div>
            <div className="summary-col-title">Distributor</div>
            <div className="summary-col-title">Customer</div>
          </div>

          <div className="summary-row">
            <div>Licenses</div>
            <div>{formatCurrency(licenseTotal)}</div>
            <div>{formatCurrency(licenseTotalCust)}</div>
          </div>
          <div className="summary-row">
            <div><span className="nowrap">Data <small>({chargedDataTb} TB)</small></span></div>
            <div>{formatCurrency(dataTotal)}</div>
            <div>{formatCurrency(dataTotalCust)}</div>
          </div>
            <div className="summary-row">
              <div>Subtotal</div>
              <div>{formatCurrency(subtotal)}</div>
              <div>{formatCurrency(subtotalCust)}</div>
            </div>

          {(isBelowMin || isBelowMinCust) && (
            <div className="notice" role="status" aria-live="polite">
              Subtotal is below minimum price — the minimum price will be applied.
            </div>
          )}

          <div className="summary-row total">
            <div><strong>Total</strong></div>
            <div><strong>{formatCurrency(appliedTotal)}</strong></div>
            <div>
              <strong
                title={
                  // Always provide tooltip (requirement); value clamped to >= 0
                  `Distributor net margin: ${formatCurrency(distributorNetMarginEur)}`
                }
              >
                {formatCurrency(appliedTotalCust)}
              </strong>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}